<template>
  <div
    :id="id"
    class="session-slide"
    :class="{
      'session-slide-relative-visual': hasVisual && visualPosition != 'fixed',
      'last-slide': isLastSlide,
    }"
    ref="sessionSlideRoot"
  >
    <div
      class="slide-wrapper-fixed-point"
      v-if="textPosition === 'dynamic'"
      ref="slideWrapperFixedPoint"
    />

    <div
      class="slide-wrapper-relative-again-point"
      v-if="textPosition === 'dynamic'"
      ref="slideWrapperRelativeAgainPoint"
    />

    <div
      class="slide-wrapper"
      :class="{
        'no-text-slide': textPosition === 'hidden',
        'full-screen-text': textPosition === 'fullScreen',
        'show-text-background': !!showTextBackground,
      }"
      ref="slideWrapper"
      :data-test="`slide-wrapper-${id}`"
    >
      <div class="slide-inner" ref="slideInner" :id="id + '-inner'">
        <slot name="text"></slot>
      </div>
    </div>
    <div class="slide-visual-wrapper" :id="id + '-visual-wrapper'" v-if="hasVisual">
      <div ref="visualFixedAgainPoint" class="visual-fixed-again-point" v-if="isDynamic"></div>

      <div
        ref="visualReadyToRecenterPoint"
        class="visual-ready-to-recenter-point"
        v-if="isDynamic"
      />

      <div ref="visualRelativePoint" class="visual-relative-point">
        <div
          class="visual side-visual text-center"
          :class="{ 'visual-always-relative': visualPosition === 'relative' }"
          ref="visualDiv"
          :id="id + '-visual'"
        >
          <slot name="visual"> </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, getCurrentInstance, useSlots, ref, nextTick } from "vue";

const props = defineProps<{
  id?: string;
  showTextBackground?: boolean;
  visualPosition?: "fixed" | "relative" | "dynamic";
  textPosition?: "relative" | "dynamic" | "fullScreen" | "hidden";
  isLastSlide?: boolean;
  classScene?: (
    element: HTMLElement,
    target: HTMLElement,
    className: string,
    index: number,
  ) => void;
}>();

const instance = getCurrentInstance()?.proxy;
const session = instance?.$parent;

const slots = useSlots();

const visualPosition = computed(() => props.visualPosition ?? "relative");
const textPosition = computed(() => props.textPosition ?? "relative");
const isLastSlide = computed(() => props.isLastSlide ?? false);
const hasVisual = computed(() => !!slots.visual);

const visualDiv = ref<HTMLElement | null>(null);
const sessionSlideRoot = ref<HTMLElement | null>(null);
const slideWrapper = ref<HTMLElement | null>(null);
const slideInner = ref<HTMLElement | null>(null);
const visualRelativePoint = ref<HTMLElement | null>(null);
const visualFixedAgainPoint = ref<HTMLElement | null>(null);
const visualReadyToRecenterPoint = ref<HTMLElement | null>(null);
const slideWrapperFixedPoint = ref<HTMLElement | null>(null);
const slideWrapperRelativeAgainPoint = ref<HTMLElement | null>(null);

const isDynamic = computed(() => visualPosition.value === "dynamic");

// The point of this proprety is to say "I am a session slide! and for the slide to identify itself"
const slideId = computed(() => props.id);

onMounted(() => {
  nextTick(() => {
    if (hasVisual.value && isDynamic.value) {
      // Locking and unlocking the strengths list. We want this completely outside the main timeline because of bugs.
      props.classScene!(
        visualRelativePoint.value as HTMLElement,
        visualDiv.value as HTMLElement,
        "visual-relative",
        0,
      );

      props.classScene!(
        visualFixedAgainPoint.value as HTMLElement,
        visualDiv.value as HTMLElement,
        "visual-fixed-again",
        0,
      );

      props.classScene!(
        visualReadyToRecenterPoint.value as HTMLElement,
        visualDiv.value as HTMLElement,
        "visual-ready-to-recenter",
        0,
      );
    }

    if (hasVisual.value && textPosition.value === "dynamic" && !(session as any).isInline) {
      props.classScene!(
        slideWrapperFixedPoint.value as HTMLElement,
        slideWrapper.value as HTMLElement,
        "slide-wrapper-fixed",
        1,
      );

      props.classScene!(
        slideWrapperRelativeAgainPoint.value as HTMLElement,
        slideWrapper.value as HTMLElement,
        "slide-wrapper-relative-again",
        1,
      );
    }
  });
});

defineExpose({
  visualDiv,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
.session.session-wide .session-slide .slide-wrapper:not(.full-screen-text) {
  float: left;
}

.session-slide .slide-wrapper {
  margin-bottom: 0px;
}

.session.session-wide .slide-visual-wrapper {
  float: left;
  float: right;
  width: 50vw;
}

.slide-visual-wrapper {
  position: relative;
}

.slide-wrapper.slide-wrapper-fixed {
  position: fixed;
  top: 0;
}
.slide-wrapper.slide-wrapper-fixed.slide-wrapper-relative-again {
  position: absolute;
  top: calc(100% - 100vh);
}

.session-slide {
  @unlockpoint: 650px;
  position: relative;

  // At this point the strengths-list becomes scrollable
  .visual-relative.visual-relative.visual-relative,
  .visual-always-relative.visual-always-relative.visual-always-relative {
    position: relative;
    top: 0;
  }

  // At this point the strengths list becomes fixed again - but it's no-longer centered.
  .visual-fixed-again.visual-fixed-again.visual-fixed-again {
    position: fixed;
    top: -@unlockpoint;
  }

  //At this point we're just about to return the strengths list to the center of the screen - so we need to set the appropriate transition time.
  .visual-ready-to-recenter.visual-ready-to-recenter.visual-ready-to-recenter {
    transition: top 1s;
    top: -@unlockpoint;
  }

  //Return the strengths list to the center of the screen.
  .visual-recentered.visual-recentered.visual-recentered {
    position: fixed;
    top: 0;
    transition: top 1s;
  }

  .visual-relative-point {
    position: relative;
    top: 0;
  }

  .slide-wrapper-fixed-point {
    top: 100vh;
    position: relative;
    width: 100%;
  }

  .slide-wrapper-relative-again-point {
    bottom: 0;
    position: absolute;
    width: 100%;
  }

  .visual-fixed-again-point {
    position: relative;
    top: @unlockpoint;
  }

  .visual-ready-to-recenter-point {
    position: relative;
    top: @unlockpoint + 200px;
  }
}

.session.session-inline {
  .session-slide-relative-visual {
    .slide-wrapper {
      margin-bottom: 20px;
    }
  }
}

.session-slide:after {
  // This is just a clear fix. But bootstrap seems to be using a newer version of clearfix which doesn't seep to work properly?
  content: " ";
  visibility: hidden;
  display: block;
  height: 0;
  clear: both;
}

.session.session-inline .slide-wrapper {
  position: relative;
  z-index: 10;
}
</style>
